import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { formatDualCurrency, formatCurrency, convertFromUSD, FX_CONFIG } from "../config/fx";
import { 
  Ship, 
  Calculator,
  Clock,
  DollarSign,
  Package,
  MapPin,
  Anchor
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "https://api.afrigulfmarket.com";

const Logistics = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [error, setError] = useState("");
  const [convertCurrency, setConvertCurrency] = useState('USD');
  const [formData, setFormData] = useState({
    origin: 'UAE',
    destination: '',
    weight_kg: '',
    volume_cbm: ''
  });

  const destinations = ['Kenya', 'Uganda', 'Tanzania', 'Ethiopia', 'Rwanda'];
  const currencyOptions = [
    { code: 'USD', label: 'US Dollar (USD)' },
    { code: 'AED', label: 'UAE Dirham (AED)' },
    { code: 'KES', label: 'Kenyan Shilling (KES)' },
    { code: 'UGX', label: 'Ugandan Shilling (UGX)' },
    { code: 'TZS', label: 'Tanzanian Shilling (TZS)' },
    { code: 'ETB', label: 'Ethiopian Birr (ETB)' },
    { code: 'RWF', label: 'Rwandan Franc (RWF)' }
  ];

  const tradeLanes = [
    { from: 'Dubai', to: 'Mombasa', days: '7-10', port: 'Jebel Ali' },
    { from: 'Abu Dhabi', to: 'Dar es Salaam', days: '8-12', port: 'Khalifa' },
    { from: 'Sharjah', to: 'Kampala', days: '10-14', port: 'Port Rashid' },
    { from: 'Dubai', to: 'Addis Ababa', days: '12-16', port: 'Jebel Ali' },
  ];

  const handleCalculate = async (e) => {
    e.preventDefault();
    setError("");
    if (!formData.destination || !formData.weight_kg) {
      const msg = 'Please fill required fields';
      setError(msg);
      toast.error(msg);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/logistics/estimate`, {
        origin: formData.origin,
        destination: formData.destination,
        weight_kg: parseFloat(formData.weight_kg),
        volume_cbm: formData.volume_cbm ? parseFloat(formData.volume_cbm) : null
      });
      setEstimate(response.data);
    } catch (err) {
      const msg = 'Failed to calculate estimate';
      setError(msg);
      toast.error(msg);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="logistics-page">
      {/* Hero */}
      <div 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(13,79,139,0.65), rgba(13,79,139,0.85)), url('https://images.unsplash.com/photo-1583737077382-3f51318d6074?w=1920&q=80')`,
          backgroundColor: '#0D4F8B'
        }}
      >
        <div className="absolute inset-0 bg-[#0D4F8B]/80" />
        <div className="container-main relative z-10 text-center">
          <span className="overline text-[#10B981] mb-3 block">{t('logistics.overline')}</span>
          <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('logistics.title')}
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto">
            Reliable shipping routes connecting UAE ports with East African destinations
          </p>
        </div>
      </div>

      <div className="container-main py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Calculator */}
          <div>
            <Card className="card-base">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#0D4F8B]/10 rounded-full flex items-center justify-center">
                    <Calculator className="w-6 h-6 text-[#0D4F8B]" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {t('logistics.calculator.title')}
                  </h2>
                </div>

                <form onSubmit={handleCalculate} className="space-y-6">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      {t('logistics.calculator.origin')}
                    </label>
                    <Input value="United Arab Emirates (UAE)" disabled className="bg-slate-100" aria-label="Origin" />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">
                      {t('logistics.calculator.destination')} *
                    </label>
                    <Select 
                      value={formData.destination} 
                      onValueChange={(v) => setFormData({ ...formData, destination: v })}
                    >
                      <SelectTrigger data-testid="logistics-destination" aria-label="Destination">
                        <SelectValue placeholder="Select destination" />
                      </SelectTrigger>
                      <SelectContent>
                        {destinations.map((dest) => (
                          <SelectItem key={dest} value={dest}>{dest}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-2 block">
                        {t('logistics.calculator.weight')} *
                      </label>
                      <Input
                        type="number"
                        placeholder="e.g., 500"
                        value={formData.weight_kg}
                        onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })}
                        aria-label="Weight in kilograms"
                        className="input-style"
                        data-testid="logistics-weight"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-2 block">
                        {t('logistics.calculator.volume')}
                      </label>
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="e.g., 2.5"
                        value={formData.volume_cbm}
                        onChange={(e) => setFormData({ ...formData, volume_cbm: e.target.value })}
                        aria-label="Volume in cubic meters"
                        className="input-style"
                        data-testid="logistics-volume"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-[#10B981] hover:bg-[#059669]"
                    disabled={loading}
                    data-testid="calculate-btn"
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    {t('logistics.calculator.estimate')}
                  </Button>
                  {error && (
                    <p className="text-sm text-red-600">{error}</p>
                  )}
                </form>

                {/* Results */}
                {estimate && (
                  <div className="mt-8 p-6 bg-[#0D4F8B]/5 rounded-md border border-[#0D4F8B]/20" data-testid="estimate-result">
                    <h3 className="font-semibold text-slate-900 mb-4">Shipping Estimate</h3>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <DollarSign className="w-6 h-6 text-[#0D4F8B] mx-auto mb-2" />
                        <div className="text-sm text-slate-500">{t('logistics.result.cost')}</div>
                        <div className="font-bold text-lg text-[#0D4F8B]">
                          {formatDualCurrency(estimate.estimated_cost_usd).usd}
                        </div>
                        <div className="text-xs text-slate-400">
                          {formatDualCurrency(estimate.estimated_cost_usd).aed}
                        </div>
                      </div>
                      <div className="text-center">
                        <Clock className="w-6 h-6 text-[#0D4F8B] mx-auto mb-2" />
                        <div className="text-sm text-slate-500">{t('logistics.result.days')}</div>
                        <div className="font-bold text-lg text-slate-900">
                          {estimate.estimated_days} days
                        </div>
                      </div>
                      <div className="text-center">
                        <Ship className="w-6 h-6 text-[#0D4F8B] mx-auto mb-2" />
                        <div className="text-sm text-slate-500">{t('logistics.result.method')}</div>
                        <div className="font-bold text-lg text-slate-900">
                          {estimate.shipping_method}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 space-y-3">
                      <div>
                        <label className="text-xs font-medium text-slate-500 block mb-1">
                          Convert estimate
                        </label>
                        <Select value={convertCurrency} onValueChange={setConvertCurrency}>
                          <SelectTrigger aria-label="Convert estimate currency">
                            <SelectValue placeholder="Select currency" />
                          </SelectTrigger>
                          <SelectContent>
                            {currencyOptions.map((currency) => (
                              <SelectItem key={currency.code} value={currency.code}>
                                {currency.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="text-center text-sm text-slate-600">
                        {formatCurrency(
                          convertFromUSD(estimate.estimated_cost_usd, convertCurrency),
                          convertCurrency
                        )}
                      </div>
                      <p className="text-xs text-slate-400 text-center">
                        {FX_CONFIG.disclaimer}
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Trade Lanes */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#10B981]/10 rounded-full flex items-center justify-center">
                <Anchor className="w-6 h-6 text-[#10B981]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {t('logistics.lanes.title')}
                </h2>
                <p className="text-sm text-slate-500">{t('logistics.lanes.desc')}</p>
              </div>
            </div>

            <div className="space-y-4">
              {tradeLanes.map((lane, index) => (
                <Card key={index} className="card-base hover-lift">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-[#0D4F8B] rounded-full flex items-center justify-center">
                          <MapPin className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">
                            {lane.from} ? {lane.to}
                          </div>
                          <div className="text-sm text-slate-500">
                            Port: {lane.port}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-[#0D4F8B] font-semibold">
                          <Clock className="w-4 h-4" />
                          {lane.days} days
                        </div>
                        <div className="text-xs text-slate-400">Transit time</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Shipping Methods */}
            <div className="mt-8 grid grid-cols-2 gap-4">
              <Card className="card-base">
                <CardContent className="p-6 text-center">
                  <Ship className="w-10 h-10 text-[#0D4F8B] mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-900 mb-1">Sea Freight</h3>
                  <p className="text-sm text-slate-500">Best for large shipments &gt;100kg</p>
                </CardContent>
              </Card>
              <Card className="card-base">
                <CardContent className="p-6 text-center">
                  <Package className="w-10 h-10 text-[#10B981] mx-auto mb-3" />
                  <h3 className="font-semibold text-slate-900 mb-1">Air Freight</h3>
                  <p className="text-sm text-slate-500">Fast delivery for urgent orders</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Logistics;



